//-- Function to parse the comment to plain text (before saving it to the database)
export function parseCommentToPlainString(html: string): string {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");
  let result = "";

  const walk = (
    node: Node,
    insideList?: { type: "ol" | "ul"; index: number }
  ) => {
    if (node.nodeType === Node.TEXT_NODE) {
      result += node.textContent;
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      const el = node as HTMLElement;
      const tag = el.tagName.toLowerCase();

      switch (tag) {
        case "br":
          result += "\n";
          break;

        case "strong":
          result += "**";
          el.childNodes.forEach((n) => walk(n, insideList));
          result += "**";
          break;

        case "em":
          result += "*";
          el.childNodes.forEach((n) => walk(n, insideList));
          result += "*";
          break;

        case "u":
          result += "__";
          el.childNodes.forEach((n) => walk(n, insideList));
          result += "__";
          break;

        case "s":
          result += "~~";
          el.childNodes.forEach((n) => walk(n, insideList));
          result += "~~";
          break;

        case "h1":
        case "h2":
        case "h3": {
          const level = Number(tag[1]);

          // ✅ Insert newline before heading if previous content doesn't end with one
          if (!result.endsWith("\n") && result.trim() !== "") {
            result += "\n";
          }

          result += `${"#".repeat(level)} `;
          el.childNodes.forEach((n) => walk(n, insideList));
          result += "\n";
          break;
        }

        case "p":
        case "div": {
          const isFileDiv = el.classList.contains("tiptap-file");

          if (!isFileDiv) {
            const insideListItem = insideList !== undefined;

            // ✅ Only add newline before/after if NOT inside a list item
            if (
              !insideListItem &&
              !result.endsWith("\n") &&
              result.trim() !== ""
            ) {
              result += "\n";
            }

            el.childNodes.forEach((n) => walk(n, insideList));

            if (!insideListItem && !result.endsWith("\n")) {
              result += "\n";
            }
          } else {
            // Handle file div
            const filename =
              el.getAttribute("data-filename") || el.textContent?.trim();
            if (filename) {
              result += `[${filename}]`;
            }
          }
          break;
        }

        case "span":
          if (el.classList.contains("mui-chip-mention")) {
            const fullname = el.getAttribute("data-fullname") || "";
            result += `@${fullname} `; // <-- Added space after mention
            //result += `[${el.getAttribute("data-id")}]`;
          } else {
            el.childNodes.forEach((n) => walk(n, insideList));
          }
          break;

        case "img": {
          const alt = el.getAttribute("data-filename")?.trim();
          const src = el.getAttribute("src");
          const filename = alt || src?.split("/").pop();
          if (filename) {
            result += `\n [${filename}]`;
          }
          break;
        }

        // case "div": {
        //   if (el.classList.contains("tiptap-file")) {
        //     const filename =
        //       el.getAttribute("data-filename") || el.textContent?.trim();
        //     if (filename) {
        //       result += `[${filename}]`;
        //     }
        //     return;
        //   }
        //   el.childNodes.forEach((n) => walk(n, insideList));
        //   break;
        // }

        case "a": {
          const href = el.getAttribute("href");
          const filename = el.getAttribute("data-filename")?.trim();

          // ✅ Safer than el.textContent to avoid merging @mentions
          const text = Array.from(el.childNodes)
            .filter(
              (n) =>
                n.nodeType === Node.TEXT_NODE ||
                ((n as HTMLElement).tagName &&
                  (n as HTMLElement).tagName.toLowerCase() === "strong")
            )
            .map((n) => n.textContent?.trim())
            .join(" ");

          if (filename || text.match(/\.(pdf|csv|docx|xlsx|txt)$/i)) {
            result += ` [${filename || text}]`;
          } else if (href && text) {
            result += `${text} (${href})`;
            //           if (text !== href) {
            //   result += `${text} (${href})`;
            // } else {
            //   result += href;
            // }
          } else {
            el.childNodes.forEach((n) => walk(n, insideList));
          }
          break;
        }

        case "ol":
          Array.from(el.children).forEach((li, i) => {
            if (li.tagName.toLowerCase() === "li") {
              result += `${i + 1}. `;
              walk(li, { type: "ol", index: i + 1 });
              result += "\n";
            }
          });
          break;

        case "ul":
          Array.from(el.children).forEach((li) => {
            if (li.tagName.toLowerCase() === "li") {
              result += `• `;
              walk(li, { type: "ul", index: 0 });
              result += "\n";
            }
          });
          break;

        case "li":
          el.childNodes.forEach((n) => walk(n, insideList));
          break;

        case "pre": {
          const codeChild = el.querySelector("code");
          const codeText = codeChild?.textContent || el.textContent || "";
          result += `\n\`\`\`\n${codeText.trim()}\n\`\`\`\n`;
          break;
        }

        default:
          el.childNodes.forEach((n) => walk(n, insideList));
          break;
      }
    }
  };

  //doc.body.childNodes.forEach((node) => walk(node));
  doc.body.childNodes.forEach((node, index, arr) => {
    walk(node);
    if (index !== arr.length - 1 && !result.endsWith("\n")) {
      result += "\n";
    }
  });

  return result.trim();
}

//-- Function to check if the editor has meaningful content
export function hasMeaningfulContent(html: string): boolean {
  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = html;

  const text = tempDiv.textContent?.trim() || "";
  const hasText = text.length > 0;
  const hasImage = tempDiv.querySelector("img") !== null;

  return hasText || hasImage;
}

//-- Function to parse inline text (mentions, bold, italic, underline, overline, emoji, link, code, image) (when retrieving the comment from the database)

// 28.07.2025
// function parseInline(text: string, allUsers: any[]) {
//   const tokens = [];
//   // Regex without heading patterns since headings are handled at block level

//   const regex =
//     /(\[(\d+)\])|(@[A-Za-z][\w]+(?:\s+[A-Za-z][\w]+)?)|\*\*(.+?)\*\*|__(.+?)__|_(.+?)_|~~([\s\S]+?)~~|(\p{Extended_Pictographic})|(\*(.+?)\*)|([A-Za-z0-9_-]+)\s*\((https?:\/\/[^\s)]+)\)|((https?:\/\/[^\s)]+))/gmu;

//   // const regex =
//   // /(\[(\d+)\])|(@[A-Za-z][\w]+(?:\s+[A-Za-z][\w]+)?)|\*\*(.+?)\*\*|__(.+?)__|_(.+?)_|~~([\s\S]+?)~~|(\p{Extended_Pictographic})|(\*(.+?)\*)|([^\n()]+?)\s*\((https?:\/\/[^\s)]+)\)|((https?:\/\/[^\s)]+))/gmu;

//   let lastIndex = 0;
//   let match;

//   while ((match = regex.exec(text)) !== null) {
//     const start = match.index;

//     if (start > lastIndex) {
//       tokens.push({ type: "text", content: text.slice(lastIndex, start) });
//     }

//     if (match[1] && match[2]) {
//       // [userId] mention
//       const userId = match[2];
//       const user = allUsers.find((u) => String(u.id) === userId);
//       if (user) {
//         tokens.push({
//           type: "mention",
//           name: `${user.first_name} ${user.last_name}`,
//           id: user.id,
//         });
//       } else {
//         tokens.push({ type: "text", content: match[1] });
//       }
//     } else if (match[3]) {
//       // @username mention
//       const fullName = match[3].slice(1).trim();
//       const user = allUsers.find(
//         (u) =>
//           `${u.first_name} ${u.last_name}`.toLowerCase() ===
//           fullName.toLowerCase()
//       );
//       if (user) {
//         tokens.push({
//           type: "mention",
//           name: `${user.first_name} ${user.last_name}`,
//           id: user.id,
//         });
//       } else {
//         tokens.push({ type: "text", content: match[3] });
//       }
//     } else if (match[4]) {
//       // **bold**
//       tokens.push({ type: "bold", content: match[4] });
//     } else if (match[5]) {
//       // __underline__
//       tokens.push({ type: "underline", content: match[5] });
//     } else if (match[6]) {
//       // _italic_
//       tokens.push({ type: "italic", content: match[6] });
//     } else if (match[7]) {
//       // ~~strikethrough~~
//       tokens.push({ type: "strikethrough", content: match[7] });
//     } else if (match[8]) {
//       // emoji
//       tokens.push({ type: "emoji", content: match[8] });
//     } else if (match[9] && match[10]) {
//       // *italic* (alternative)
//       tokens.push({ type: "italic", content: match[10] });
//     } else if (match[11] && match[12]) {
//       // linkText (url) format
//       tokens.push({
//         type: "link",
//         href: match[12],
//         content: match[11], // Display text
//       });
//     } else if (match[13]) {
//       // standalone links
//       const cleanHref = match[13].replace(/[)\]]+$/, "");
//       tokens.push({ type: "link", href: cleanHref, content: cleanHref });
//     }

//     lastIndex = regex.lastIndex;
//   }

//   if (lastIndex < text.length) {
//     tokens.push({ type: "text", content: text.slice(lastIndex) });
//   }

//   return tokens;
// }

function parseInline(text: string, allUsers: any[] = []): any[] {
  const tokens = [];
  let lastIndex = 0;

  const formattingRegex =
    /(\*\*\*([\s\S]+?)\*\*\*)|(\*\*([\s\S]+?)\*\*)|(__([\s\S]+?)__)|(_([\s\S]+?)_)|(\*([\s\S]+?)\*)|(~~([\s\S]+?)~~)|(@[A-Za-z][\w]+(?:\s+[A-Za-z][\w]+)?)|\[(\d+)\]|(\p{Extended_Pictographic})|([A-Za-z0-9_-]+)\s*\((https?:\/\/[^\s)]+)\)|((https?:\/\/[^\s)]+))/gmu;

  const pushText = (str: string) => {
    if (str) tokens.push({ type: "text", content: str });
  };

  let match;
  while ((match = formattingRegex.exec(text)) !== null) {
    const start = match.index;
    if (start > lastIndex) pushText(text.slice(lastIndex, start));

    if (match[1]) {
      // ***bold italic***
      tokens.push({
        type: "bold_italic",
        content: parseInline(match[2], allUsers),
      });
    } else if (match[3]) {
      // **bold**
      tokens.push({
        type: "bold",
        content: parseInline(match[4], allUsers),
      });
    } else if (match[5]) {
      // __underline__
      tokens.push({
        type: "underline",
        content: parseInline(match[6], allUsers),
      });
    } else if (match[7]) {
      // _italic_
      tokens.push({
        type: "italic",
        content: parseInline(match[8], allUsers),
      });
    } else if (match[9]) {
      // *italic*
      tokens.push({
        type: "italic",
        content: parseInline(match[10], allUsers),
      });
    } else if (match[11]) {
      // ~~strikethrough~~
      tokens.push({
        type: "strikethrough",
        content: parseInline(match[12], allUsers),
      });
    } else if (match[13]) {
      // @mention
      const name = match[13].slice(1).trim();
      const user = allUsers.find(
        (u) =>
          `${u.first_name} ${u.last_name}`.toLowerCase() === name.toLowerCase()
      );
      if (user) {
        tokens.push({
          type: "mention",
          name: `${user.first_name} ${user.last_name}`,
          id: user.id,
        });
      } else {
        tokens.push({ type: "text", content: match[13] });
      }
    } else if (match[14]) {
      // [userId]
      const userId = match[14];
      const user = allUsers.find((u) => String(u.id) === userId);
      tokens.push(
        user
          ? {
              type: "mention",
              name: `${user.first_name} ${user.last_name}`,
              id: user.id,
            }
          : { type: "text", content: `[${userId}]` }
      );
    } else if (match[15]) {
      // emoji
      tokens.push({ type: "emoji", content: match[15] });
    } else if (match[16] && match[17]) {
      // linkText (url)
      tokens.push({ type: "link", href: match[17], content: match[16] });
    } else if (match[18]) {
      // plain url
      tokens.push({ type: "link", href: match[18], content: match[18] });
    }

    lastIndex = formattingRegex.lastIndex;
  }

  if (lastIndex < text.length) {
    pushText(text.slice(lastIndex));
  }

  return tokens;
}

export function parseCommentText(
  rawComment: string,
  allUsers: any[],
  filesList: any
): any[] {
  const blocks: any[] = [];
  const lines = rawComment?.split(/\n/);

  let inCodeBlock = false;
  let currentCodeLines: string[] = [];

  for (let i = 0; i < lines?.length; i++) {
    const line = lines[i];

    // Toggle code block mode
    if (line.trim().startsWith("```")) {
      if (inCodeBlock) {
        // End of code block
        blocks.push({
          type: "code_block",
          code: currentCodeLines.join("\n"),
        });
        currentCodeLines = [];
        inCodeBlock = false;
      } else {
        // Start of code block
        inCodeBlock = true;
        currentCodeLines = [];
      }
      continue;
    }

    if (inCodeBlock) {
      currentCodeLines.push(line);
      continue;
    }

    const trimmedLine = line.trim();

    // ⛔ Skip "📄 PDF Preview" line
    if (
      trimmedLine === "📄 PDF Preview" ||
      trimmedLine === ":page_facing_up: PDF Preview"
    ) {
      continue;
    }
    if (!trimmedLine) continue;

    // Check for headings FIRST (before other parsing)
    const headingMatch = trimmedLine.match(/^(#{1,3}) (.+)$/);
    if (headingMatch) {
      const level = headingMatch[1].length;
      const content = headingMatch[2];

      blocks.push({
        type: level === 1 ? "h1" : level === 2 ? "h2" : "h3",
        content: parseInline(content, allUsers), // Parse inline content including mentions
      });
      continue;
    }

    // Ordered List
    //const orderedMatch = trimmedLine.match(/^(\d+)\.\s+(.+)$/);
    const orderedMatch = trimmedLine.match(/^(\d+)[.)]\s+(.+)$/);
    if (orderedMatch) {
      blocks.push({
        type: "ordered-list-item",
        order: parseInt(orderedMatch[1], 10),
        content: parseInline(orderedMatch[2], allUsers),
      });
      continue;
    }

    // Unordered List
    //const unorderedMatch = trimmedLine.match(/^[-*]\s+(.+)$/);
    const unorderedMatch = trimmedLine.match(/^[•\-*]\s+(.+)$/);
    if (unorderedMatch) {
      blocks.push({
        type: "unordered-list-item",
        content: parseInline(unorderedMatch[1], allUsers),
      });
      continue;
    }

    let matched = false;
    // ✅ Detect images (jpg, jpeg, png, gif, webp)
    const imageMatches = [
      ...trimmedLine.matchAll(/\[([^\]]+\.(jpg|jpeg|png|gif|webp))\]/gi),
    ];

    if (imageMatches.length > 0) {
      matched = true;
      imageMatches.forEach((match) => {
        const filename = match[1];
        const matchedFile = filesList?.find(
          (f: any) =>
            f.file.includes(encodeURIComponent(filename)) ||
            f.file.includes(filename)
        );

        blocks.push({
          type: "image",
          filename,
          src: matchedFile?.file ?? filename,
          timestamp: matchedFile?.timestamp ?? null, // Optional
        });
      });
    }

    // this for files (pdf, csv, xlsx, docx, txt)
    const fileMatches = [
      ...trimmedLine.matchAll(/\b([\w.-]+?\.(pdf|csv|xlsx|xls|docx|txt))\b/gi),
    ];

    if (fileMatches.length > 0) {
      matched = true;

      fileMatches.forEach((match) => {
        const matchedFilename = match[1];
        const filename = matchedFilename.startsWith("Preview")
          ? matchedFilename.replace(/^Preview/, "")
          : matchedFilename;
        const matchedFile = filesList?.find(
          (f: any) =>
            f.file.includes(encodeURIComponent(filename)) ||
            f.file.includes(filename)
        );
        blocks.push({
          type: "file",
          filename,
          src: matchedFile?.file ?? filename,
          timestamp: matchedFile?.timestamp ?? null,
        });
      });
    }

    if (matched) {
      let cleanedLine = trimmedLine;

      // Remove matched image markdown: [image.png]
      imageMatches.forEach((match) => {
        cleanedLine = cleanedLine.replace(match[0], "");
      });

      // Remove blob links like: (blob:...)
      cleanedLine = cleanedLine.replace(/\(blob:[^)]+\)/gi, "");

      // Remove known patterns like: PDF**filename.pdf**Download
      cleanedLine = cleanedLine.replace(
        /PDF\*\*.*?\.(pdf|csv|xlsx|xls|docx|txt)\*\*Download/gi,
        ""
      );

      // Remove remaining file names that were matched already
      fileMatches.forEach((match) => {
        cleanedLine = cleanedLine.replace(match[0], "");
      });

      // Remove extra leftover text like "Download"
      cleanedLine = cleanedLine.replace(/\bDownload\b/gi, "");

      const inlineTokens = parseInline(cleanedLine.trim(), allUsers);
      if (inlineTokens.length) {
        blocks.push({
          type: "paragraph",
          content: inlineTokens,
        });
      }

      continue;
    }

    // Legacy [code_block]
    if (trimmedLine === "[code_block]") {
      blocks.push({ type: "code_block" });
      continue;
    }

    // Default: Paragraph
    blocks.push({
      type: "paragraph",
      content: parseInline(trimmedLine, allUsers),
    });
  }

  return blocks;
}

//---------------------This function is used for comment edit section---------------------
//-- Function to escape HTML
function escapeHtml(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

//--Function to parse inline content
// // 28.07.2025
// function renderInlineContent(content: any[] = []): string {
//   return content
//     .map((child) => {
//       switch (child.type) {
//         case "text":
//           return escapeHtml(child.content);
//         case "mention":
//           return `<span class="mui-chip-mention" data-id="${child.id}" data-fullname="${child.name}">@${child.name}</span>`;
//         case "bold":
//           return `<strong>${escapeHtml(child.content)}</strong>`;
//         case "italic":
//           return `<em>${escapeHtml(child.content)}</em>`;
//         case "underline":
//           return `<u>${escapeHtml(child.content)}</u>`;
//         case "strikethrough":
//           return `<s>${escapeHtml(child.content)}</s>`;
//         case "overline":
//           return `<span style="text-decoration: overline">${escapeHtml(
//             child.content
//           )}</span>`;
//         case "emoji":
//           return `${child.content}`; // Already a unicode character
//         case "link":
//           return `<a href="${child.href}" target="_blank">${escapeHtml(
//             child.content
//           )}</a>`;
//         default:
//           return "";
//       }
//     })
//     .join("");
// }

function renderInlineContent(content: any[] = []): string {
  return content
    .map((child) => {
      switch (child.type) {
        case "text":
          return escapeHtml(child.content);
        case "mention":
          return `<span class="mui-chip-mention" data-id="${child.id}" data-fullname="${child.name}">@${child.name}</span>`;
        case "bold":
          return `<strong>${renderInlineContent(child.content)}</strong>`;
        case "italic":
          return `<em>${renderInlineContent(child.content)}</em>`;
        case "underline":
          return `<u>${renderInlineContent(child.content)}</u>`;
        case "bold_italic":
          return `<span style="font-weight: 600; font-style: italic;">${renderInlineContent(
            child.content
          )}</span>`;

        case "bold_underline":
          return `<span style="font-weight: 600; text-decoration: underline;">${renderInlineContent(
            child.content
          )}</span>`;

        case "italic_underline":
          return `<span style="font-style: italic; text-decoration: underline;">${renderInlineContent(
            child.content
          )}</span>`;

        case "bold_italic_underline":
          return `<span style="font-weight: 600; font-style: italic; text-decoration: underline;">${renderInlineContent(
            child.content
          )}</span>`;
        case "strikethrough":
          return `<s>${renderInlineContent(child.content)}</s>`;
        case "overline":
          return `<span style="text-decoration: overline">${renderInlineContent(
            child.content
          )}</span>`;
        case "emoji":
          return `${child.content}`;
        case "link":
          return `<a href="${child.href}" target="_blank">${escapeHtml(
            child.content
          )}</a>`;
        default:
          return `<span class="unknown-token">${escapeHtml(
            typeof child.content === "string" ? child.content : ""
          )}</span>`;
      }
    })
    .join("");
}

export function convertTiptapNodesToHTML(nodes: any[]): string {
  let html = "";
  let insideOl = false;
  let insideUl = false;

  for (const node of nodes) {
    switch (node.type) {
      case "h1":
        html += `<h1>${renderInlineContent(node.content)}</h1>`;
        break;

      case "h2":
        html += `<h2>${renderInlineContent(node.content)}</h2>`;
        break;

      case "h3":
        html += `<h3>${renderInlineContent(node.content)}</h3>`;
        break;

      case "paragraph":
        html += `<p>${renderInlineContent(node.content)}</p>`;
        break;

      case "code_block":
        html += `<pre><code>${escapeHtml(node.code || "")}</code></pre>`;
        break;

      case "image":
        html += `<img src="${node.src}" data-filename="${node.filename}" />`;
        break;

      case "file":
        html += `<div class="tiptap-file"
          data-type="file-block"
          data-filename="${node.filename}"
          data-src="${node.src}"
          onclick="window.open('${node.src}', '_blank')"
          style="
            display: inline-block;
            width: 200px;
            height: 150px;
            margin-right: 8px;
            padding: 8px;
            border: 1px solid #ccc;
            border-radius: 8px;
            cursor: pointer;
            text-align: center;
            overflow: hidden;
          ">
          <div style='height: 100px; display: flex; align-items: center; justify-content: center; background: #f5f5f5; color: #888;'>📄</div>
          <div style='font-size: 13px; font-weight: 500; margin-top: 4px; word-break: break-all;'>${node.filename}</div>
        </div>`;
        break;

      case "ordered-list-item":
        if (!insideOl) {
          html += "<ol>";
          insideOl = true;
        }
        html += `<li>${renderInlineContent(node.content)}</li>`;
        break;

      case "unordered-list-item":
        if (!insideUl) {
          html += "<ul>";
          insideUl = true;
        }
        html += `<li>${renderInlineContent(node.content)}</li>`;
        break;

      case "mention":
        html += `<span data-type="mention" data-id="${node.id}">@${node.name}</span>`;
        break;

      default:
        break;
    }

    // Close list if next node is not a list item
    const nextNode = nodes[nodes.indexOf(node) + 1];
    if (insideOl && (!nextNode || nextNode.type !== "ordered-list-item")) {
      html += "</ol>";
      insideOl = false;
    }
    if (insideUl && (!nextNode || nextNode.type !== "unordered-list-item")) {
      html += "</ul>";
      insideUl = false;
    }
  }

  return html;
}
